// Shared by the reservation / event booking forms and their API routes, so
// the client can mark invalid fields before submitting and the server applies
// the exact same rules.

export type BookingField = "name" | "phone" | "email" | "people" | "time" | "date";
export type BookingFieldErrors = Partial<Record<BookingField, string>>;

const asTrimmedString = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

export function validateBooking(
  input: Partial<Record<BookingField, unknown>>
): BookingFieldErrors {
  const errors: BookingFieldErrors = {};
  const people = Number(asTrimmedString(input.people));

  if (!asTrimmedString(input.name)) {
    errors.name = "Bitte gib deinen Namen ein.";
  }
  if (!asTrimmedString(input.phone)) {
    errors.phone = "Bitte gib deine Telefonnummer ein.";
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(asTrimmedString(input.email))) {
    errors.email = "Bitte gib eine gültige E-Mail-Adresse ein.";
  }
  if (!Number.isInteger(people) || people < 1) {
    errors.people = "Bitte gib eine gültige Anzahl Personen ein (mindestens 1).";
  }
  if (!asTrimmedString(input.time)) {
    errors.time = "Bitte gib eine Uhrzeit ein.";
  }
  if (Number.isNaN(Date.parse(asTrimmedString(input.date)))) {
    errors.date = "Bitte wähle ein Datum aus.";
  }

  return errors;
}

export const hasBookingErrors = (errors: BookingFieldErrors) =>
  Object.values(errors).some(Boolean);

// Spread onto an input so it gets the red invalid styling and screen readers
// announce the message rendered by <FieldError id={`${idPrefix}-error`} />.
export const invalidFieldProps = (
  errors: BookingFieldErrors,
  field: BookingField,
  idPrefix: string = field
) =>
  errors[field]
    ? { "aria-invalid": true, "aria-describedby": `${idPrefix}-error` }
    : {};

// Storyblok only accepts a-z, 0-9 and "-" in slugs, so names like
// "Jean-Pierre & Barbara" or "Müller" have to be transliterated first —
// otherwise the Management API rejects the story and the booking fails.
export function createBookingSlug(name: string) {
  const base = name
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
    .replace(/-+$/, "");

  return `${base || "buchung"}-${Date.now()}`;
}

// Vercel functions run in UTC, so the client's local-midnight ISO string
// would otherwise be formatted as the previous day.
export const formatBookingDate = (date: string) =>
  new Date(date).toLocaleDateString("de-DE", { timeZone: "Europe/Zurich" });

export const BOOKING_INVALID_MESSAGE = "Bitte überprüfe die rot markierten Felder.";
export const BOOKING_FAILED_MESSAGE =
  "Deine Anfrage konnte leider nicht übermittelt werden. Bitte versuche es später nochmals oder kontaktiere uns telefonisch.";
// The booking is stored in Storyblok, only the mail to us failed: asking the
// guest to try again would book twice.
export const BOOKING_MAIL_FAILED_MESSAGE =
  "Deine Anfrage ist bei uns gespeichert, die Benachrichtigung per E-Mail ist aber fehlgeschlagen. Bitte ruf uns kurz an, damit wir sie sicher sehen.";
